import { Language, Lightning, Profile } from '@lightningjs/sdk'
import { appSettings } from './AppSettings'

export const truncateByMaxCharacterLength = (text, maxCharacters) => {
  if (!(text && text.length)) {
    return ''
  }
  if (text.length <= maxCharacters) {
    return text
  } else {
    return `${text.substr(0, maxCharacters)}...`
  }
}

/**
 * @param checkArray {array}
 * @returns {boolean} true / false
 */
export const isFilledArray = checkArray => {
  return !!(checkArray && Array.isArray(checkArray) && checkArray.length > 0)
}

/**
 * @param checkObject {object}
 * @returns {boolean} true / false
 */
export const isFilledObject = checkObject => {
  if (checkObject === undefined || Array.isArray(checkObject) === true) {
    return false
  } else {
    return !(Object.keys(checkObject).length === 0 && checkObject.constructor === Object)
  }
}

/**
 * @param width
 * @param height
 * @returns {string}
 */
export const getAspectRatio = (width, height) => {
  return (width / height).toFixed(1)
}

export const getProgressBarWidth = (barTotalWidth = 0, duration = 0, progress = 0) => {
  let percentage = progress / Math.max(duration, 1)
  return (barTotalWidth / 100) * (percentage * 100)
}

export const getTimeBeautified = timeInSec => {
  let seconds = parseInt(timeInSec % 60)
  let minutes = parseInt((timeInSec / 60) % 60)
  let hours = parseInt((timeInSec / 60 / 60) % 24)

  seconds = seconds < 10 ? '0' + seconds : seconds
  minutes = minutes < 10 ? '0' + minutes : minutes
  hours = hours < 10 ? '0' + hours : hours
  return `${hours !== '00' ? hours + ':' : ''}${minutes}:${seconds}`
}

export const getDateBeautified = time => {
  let date = new Date(time)

  let month = date.getMonth() < 10 ? `0${date.getMonth()}` : date.getMonth()
  let day = date.getDay() < 10 ? `0${date.getDay()}` : date.getDay()
  let year = date.getFullYear()

  return `${month}/${day}/${year}`
}

export const daysLeft = time => {
  let date = new Date(time)
  let curDate = new Date()
  let timeLeftSec =
    date.getTime() > curDate.getTime() ? (date.getTime() - curDate.getTime()) / 1000 : 0
  return parseInt((timeLeftSec / 60 / 60 / 24) % 365)
}
export const getForYouCoverTitle = type => {
  if (type === 'flow') {
    return Language.translate('MusicFlowTitle')
  } else {
    switch (type) {
      case 'flow':
        return Language.translate('MusicFlowTitle')
      case 'chill':
        return Language.translate('MusicChillTitle')
      case 'discovery':
        return Language.translate('MusicDiscoverTitle')
      case 'inspired-by-1':
      case 'inspired-by-2':
      case 'inspired-by-3':
      case 'inspired-by-4':
      case 'inspired-by-5':
        return Language.translate('MusicDailyTitle')
    }
  }
}

export const isFunction = v => {
  return typeof v === 'function'
}

export const isObject = v => {
  return typeof v === 'object'
}

export const isPage = (v, stage) => {
  return v instanceof Lightning.Element || isConstructor(v, stage)
}

export const isConstructor = (type, stage) => {
  try {
    stage.c({ type })
  } catch (e) {
    return false
  }
  return true
}

export const isArray = v => {
  return Array.isArray(v)
}

// not es5 compatible
export const isClass = v => {
  return isFunction(v) && /^\s*class\s+/i.test(v.toString())
}

export const ucfirst = v => {
  return `${v.charAt(0).toUpperCase()}${v.slice(1)}`
}

// my utils
export const formatDate = (dt, options = { year: 'numeric', month: 'long', day: 'numeric' }) => {
  return dt.toLocaleDateString(Profile.language(), options)
}

export const formatTime = seconds => {
  const hours = Math.floor(seconds / 3600)
  seconds -= hours * 3600
  const minutes = Math.floor(seconds / 60)
  seconds -= minutes * 60
  seconds = Math.floor(seconds)

  const parts = []

  if (hours) {
    parts.push(hours + 'h')
  }
  if (minutes) {
    parts.push(minutes + 'm')
  }
  if (seconds) {
    parts.push(seconds < 10 ? '0' + seconds + 's' : '' + seconds + 's')
  }

  return parts.join(' ')
}

export const formatTimeHoursMinutes = seconds => {
  const hours = Math.floor(seconds / 3600)
  seconds -= hours * 3600
  const minutes = Math.floor(seconds / 60)

  const parts = []
  parts.push(hours)
  parts.push(minutes)

  return parts.map(number => (number < 10 ? '0' + number : '' + number)).join(':')
}

export const formatTimeMinutes = seconds => {
  const minutes = Math.floor(seconds / 60)

  return minutes < 10 ? `0${minutes}` : minutes
}

export const formatTimeMinutesSeconds = seconds => {
  const minutes = Math.floor(seconds / 60),
    parts = []

  seconds -= minutes * 60
  seconds = Math.floor(seconds)
  if (minutes) {
    parts.push(minutes + 'm')
  }
  if (seconds) {
    parts.push(seconds < 10 ? '0' + seconds + 's' : '' + seconds + 's')
  }

  return parts.join(' ')
}

export const formatTimeHoursOrMinutes = seconds => {
  return seconds > 3600 ? formatTime(seconds) : formatTimeMinutesSeconds(seconds)
}

export const formatNumber = (num, type = 'short') => {
  if (type === 'short') {
    if (isNaN(num)) {
      return num
    }

    if (num < 999) {
      return num
    }

    if (num < 9999) {
      return Math.round(num / 1000) + 'K'
    }

    if (num < 1000000) {
      return Math.round(num / 1000) + 'K'
    }

    if (num < 10000000) {
      return (num / 1000000).toFixed(2) + 'M'
    }

    if (num < 1000000000) {
      return Math.round(num / 1000000) + 'M'
    }

    if (num < 1000000000000) {
      return Math.round(num / 1000000000) + 'B'
    }

    return '1T+'
  } else {
    return new Intl.NumberFormat(appSettings.isoLocale).format(num)
  }
}

export const limitWithinRange = (num, min, max) => {
  return Math.min(Math.max(num, min), max)
}

export const isNumber = v => {
  return typeof v === 'number'
}

export const isInteger = value => {
  return isNumber(value) && value % 1 === 0
}

export const isFloat = value => {
  return isNumber(value) && value % 1 !== 0
}

export const isBoolean = v => {
  return typeof v === 'boolean'
}

export const isString = v => {
  return typeof v === 'string'
}

export const camelize = str => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
      return index === 1 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, '')
}

export const cleanHtml = html => {
  let resultText = ''

  if (typeof html !== 'undefined') {
    const regexP = /(<\/p>)/gi
    const regexElements = /<\/?[a-z][a-z0-9]*[^<>]*>|<!--.*?-->/gi
    const regexWhiteSpace = /(\s){2,}/gi
    const regexQuote = /(&#039;)/gi

    resultText = html.replace(regexP, '\n\n')

    resultText = resultText.replace(regexElements, '')
    resultText = resultText.replace(regexWhiteSpace, '\n\n')
    resultText = resultText.replace(regexQuote, "'")
  }

  return resultText
}

// array of objects sort helpers
export const addItemsIndex = dataItems => {
  if (dataItems) {
    dataItems.map((res, idx) => {
      res.index = idx
    })
  }

  return dataItems
}

export const dynamicSort = property => {
  let sortOrder = 1

  if (property && property[0] === '-') {
    sortOrder = -1
    property = property.substr(1)
  }

  return function(a, b) {
    let result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0

    return result * sortOrder
  }
}

export const shuffle = array =>
  array
    .map(a => ({ sort: Math.random(), value: a }))
    .sort((a, b) => a.sort - b.sort)
    .map(a => a.value)
export const itemBackgroundUrl = id =>
  `https://www.globalplayer.com/assets/backgrounds/${id}/desktop.jpg?v0.1`
export const rotateArray = (data, t) => {
  if (!isFilledArray(data)) {
    return data
  }

  let index = -1

  while (++index < t) {
    data.unshift(data.pop())
  }

  return data
}
